import { useEffect, useRef, useState } from 'react';
import {
  createIdleTracker,
  ACTIVITY_EVENTS,
  WARNING_LEAD_MS,
} from '../utils/idleTracker';

/**
 * React glue for the idle tracker (v3.56.0).
 *
 * Mounted ONCE, in App.js, inside the authenticated layout — not in a page
 * component. Every authenticated route is therefore covered, including
 * routes a teacher sits on for a long time (an open assessment) and admin
 * routes.
 *
 * The hook owns only wiring: DOM listeners, the visibilitychange resync,
 * and the two pieces of render state the modal needs. All timing logic
 * lives in utils/idleTracker.js so it can be unit-tested without React.
 *
 * @param {boolean} enabled   only true when a user is authenticated
 * @param {(reason: string) => void} onExpire  session teardown (App.js)
 */
export default function useIdleTimeout(enabled, onExpire) {
  const [warningRemainingMs, setWarningRemainingMs] = useState(null);
  const trackerRef = useRef(null);
  // Keep the latest callback without restarting the tracker on every render.
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    if (!enabled) {
      setWarningRemainingMs(null);
      return undefined;
    }

    let expiredLatch = false;
    const tracker = createIdleTracker({
      onWarn: (remaining) => setWarningRemainingMs(remaining),
      onWarnCleared: () => setWarningRemainingMs(null),
      onTick: (remaining) => {
        // Only drive re-renders inside the warning window.
        if (remaining <= WARNING_LEAD_MS) setWarningRemainingMs(remaining);
      },
      onExpire: (reason) => {
        if (expiredLatch) return;
        expiredLatch = true;
        setWarningRemainingMs(null);
        onExpireRef.current?.(reason);
      },
    });
    trackerRef.current = tracker;
    tracker.start();

    const onActivity = () => tracker.recordActivity();
    ACTIVITY_EVENTS.forEach((evt) => {
      // passive: these listeners never preventDefault, and scroll/touchstart
      // are performance-sensitive on the classroom iPhones.
      window.addEventListener(evt, onActivity, { passive: true });
    });

    // A backgrounded tab's interval is throttled to ~1/min (or paused
    // entirely); recompute from the stored timestamp the instant it wakes.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') tracker.syncNow();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);

    // BroadcastChannel fallback for browsers without it.
    const onStorage = (event) => tracker.handleStorageEvent(event);
    window.addEventListener('storage', onStorage);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
      window.removeEventListener('storage', onStorage);
      tracker.stop();
      trackerRef.current = null;
    };
  }, [enabled]);

  return {
    /** non-null only while the warning modal should be open */
    warningRemainingMs,
    /** "Stay signed in" */
    extendSession: () => {
      trackerRef.current?.extend();
      setWarningRemainingMs(null);
    },
    /** Explicit logout elsewhere in the app — sign every tab out. */
    broadcastSignOut: (reason = 'logout') => trackerRef.current?.signOutAllTabs(reason),
    /** Clear the shared timestamp after teardown. */
    clearIdleState: () => trackerRef.current?.clearStoredState(),
  };
}
